import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TextField, Button, Menu, MenuItem } from '@mui/material';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';

import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Divider,
    CircularProgress,
    Breadcrumbs,
    Link as MuiLink,
} from '@mui/material';
import { MAIN_URL } from '../../../Configurations/Urls';
import Header from '../../DataLayouts/Header';
import { Business } from '@mui/icons-material';

// Consistent date formatter: YYYY-MM-DD
const formatDate = (dateString) => {
    if (!dateString) return "-";
    const dateObj = new Date(dateString);
    if (isNaN(dateObj.getTime())) return "-";
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** Decode MINUTES.SECONDS float from API mode=1 (e.g. 15.45 = 15 min 45 sec). */
const parseMinuteSecondsValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") return 0;
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    const minutes = Math.floor(num);
    const seconds = Math.round((num - minutes) * 100);
    return minutes + Math.round(seconds / 60);
};

const getTaskTitle = (task) => task.project_task_name ?? task.task_title ?? "";

const getAssignedPersonName = (task) => {
    const flat = task.assigned_to_employee ?? task.assigned_employee;
    if (flat && flat !== "-") return flat;
    const person = task.assigned_organization_person?.person;
    if (!person) return "";
    return person.full_name || `${person.first_name || ""} ${person.last_name || ""}`.trim();
};

const getCategoryName = (task) => {
    if (task.task_category && task.task_category !== "-") return task.task_category;
    return task.category?.task_category_name ?? "";
};

const getEstimatedMinutesRaw = (task) => {
    if (task.allocated_minutes !== undefined && task.allocated_minutes !== null && task.allocated_minutes !== "-") {
        return parseMinuteSecondsValue(task.allocated_minutes);
    }
    if (typeof task.estimated_minutes === "number") return task.estimated_minutes;
    if (typeof task.total_estimated_minutes === "number") return task.total_estimated_minutes;
    return null;
};

const getLoggedMinutesRaw = (task) => {
    if (task.total_calculated_minutes !== undefined && task.total_calculated_minutes !== null && task.total_calculated_minutes !== "-") {
        const v = task.total_calculated_minutes;
        return typeof v === "number" ? v : parseInt(v, 10) || 0;
    }
    if (Array.isArray(task.time_logs) && task.time_logs.length > 0) {
        return task.time_logs.reduce((sum, log) => sum + (log.total_minutes || 0), 0);
    }
    if (Array.isArray(task.schedules) && task.schedules.length > 0) {
        return task.schedules
            .flatMap((sch) => (Array.isArray(sch.time_logs) ? sch.time_logs : []))
            .reduce((sum, log) => sum + (log.total_minutes || 0), 0);
    }
    return 0;
};

const getSchedulesDisplay = (task) => {
    const scheduleDates = Array.isArray(task.schedules) && task.schedules.length > 0
        ? task.schedules.map((s) => s.scheduled_date).filter(Boolean)
        : [];
    if (scheduleDates.length > 1) {
        return scheduleDates.map(formatDate).join(", ");
    }
    const dateToUse = scheduleDates[0] || task.scheduled_date;
    if (!dateToUse || dateToUse === "-") return "-";
    if (typeof dateToUse === "string" && dateToUse.includes("multiple")) return dateToUse;
    // mode=1 returns d/m/Y strings
    if (typeof dateToUse === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateToUse)) {
        const [day, month, year] = dateToUse.split("/");
        return formatDate(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`);
    }
    return formatDate(dateToUse);
};

const getScheduleDateForFilter = (task) => {
    const scheduleDates = Array.isArray(task.schedules) && task.schedules.length > 0
        ? task.schedules.map((s) => s.scheduled_date).filter(Boolean)
        : [];
    if (scheduleDates[0]) return scheduleDates[0];
    const sd = task.scheduled_date;
    if (!sd || sd === "-" || (typeof sd === "string" && sd.includes("multiple"))) return null;
    if (typeof sd === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(sd)) {
        const [day, month, year] = sd.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return sd;
};

const minsToHrMin = (minutes) => {
    const min = Number(minutes);
    if (Number.isNaN(min) || min < 0) return "-";
    const hr = Math.floor(min / 60);
    const remMin = min % 60;
    const out = [];
    if (hr > 0) out.push(`${hr} hr`);
    if (remMin > 0) out.push(`${remMin} min`);
    if (out.length === 0) out.push("0 min");
    return out.join(" ");
};

// Helper to convert table data to CSV string
const convertTasksToCSV = (tasks) => {
    if (!Array.isArray(tasks) || tasks.length === 0) return "";
    const header = [
        "Task Title",
        "Assigned Person",
        "Estimated Minutes",
        "Quantity",
        "Logged Minutes",
        "Category",
        "Schedules"
    ];
    const rows = tasks.map((task) => {
        const assignedPerson = getAssignedPersonName(task);
        const estBase = getEstimatedMinutesRaw(task);
        const estimatedMinutes = estBase !== null
            ? estBase * (typeof task.quantity === "number" && task.quantity > 0 ? task.quantity : 1)
            : "-";
        const quantity = typeof task.quantity !== "undefined" && task.quantity !== null && task.quantity !== "-"
            ? task.quantity
            : "-";
        const loggedMinutes = getLoggedMinutesRaw(task);
        const category = getCategoryName(task);
        const schedules = getSchedulesDisplay(task);
        return [
            getTaskTitle(task),
            assignedPerson,
            estimatedMinutes,
            quantity,
            loggedMinutes,
            category,
            schedules
        ]
            .map((val) =>
                typeof val === "string" && (val.includes(",") || val.includes('"'))
                    ? `"${val.replace(/"/g, '""')}"`
                    : val
            )
            .join(",");
    });
    return [header.join(","), ...rows].join("\r\n");
};

const convertTasksToHTML = (tasks) => {
    if (!Array.isArray(tasks) || tasks.length === 0) return "";
    const header = [
        "Task Title",
        "Assigned Person",
        "Estimated Minutes",
        "Quantity",
        "Logged Minutes",
        "Category",
        "Schedules"
    ];
    let html = `
<table border="1" cellpadding="4" cellspacing="0">
    <thead>
        <tr>${header.map(h => `<th>${h}</th>`).join("")}</tr>
    </thead>
    <tbody>
    ${tasks.map(task => {
        const assignedPerson = getAssignedPersonName(task);
        const estBase = getEstimatedMinutesRaw(task);
        const estimatedMinutes = estBase !== null
            ? estBase * (typeof task.quantity === "number" && task.quantity > 0 ? task.quantity : 1)
            : "-";
        const quantity = typeof task.quantity !== "undefined" && task.quantity !== null && task.quantity !== "-"
            ? task.quantity
            : "-";
        const loggedMinutes = getLoggedMinutesRaw(task);
        const category = getCategoryName(task);
        const schedules = getSchedulesDisplay(task);
        return `<tr>
            <td>${getTaskTitle(task)}</td>
            <td>${assignedPerson}</td>
            <td>${estimatedMinutes}</td>
            <td>${quantity}</td>
            <td>${loggedMinutes}</td>
            <td>${category}</td>
            <td>${schedules}</td>
        </tr>`;
    }).join("")}
    </tbody>
</table>
    `;
    return html;
};

const convertTasksToXLS = (tasks) => {
    // Use the HTML table string, but save as .xls for Excel to read it.
    const html = convertTasksToHTML(tasks);
    const xlsHeader = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
            <!--[if gte mso 9]>
                <xml>
                    <x:ExcelWorkbook>
                        <x:ExcelWorksheets>
                            <x:ExcelWorksheet>
                                <x:Name>Tasks</x:Name>
                                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                            </x:ExcelWorksheet>
                        </x:ExcelWorksheets>
                    </x:ExcelWorkbook>
                </xml>
            <![endif]-->
        </head>
        <body>
    `;
    const xlsFooter = `</body></html>`;
    return xlsHeader + html + xlsFooter;
};


// -------------------------------------
// PDF export using jsPDF and html2canvas
// -------------------------------------


async function exportTasksToPDF(tasks, filename = "ProjectTasks.pdf") {
    // We'll mount the HTML table somewhere hidden, then snapshot.
    // Create container
    const hiddenDiv = document.createElement('div');
    hiddenDiv.style.position = 'fixed';
    hiddenDiv.style.left = '-99999px';
    hiddenDiv.style.top = '0';
    hiddenDiv.style.zIndex = '-1';
    hiddenDiv.style.background = '#fff';
    hiddenDiv.innerHTML = `<h3>Project Tasks</h3>${convertTasksToHTML(tasks)}`;
    document.body.appendChild(hiddenDiv);

    // Wait for DOM render, then snapshot
    const canvas = await html2canvas(hiddenDiv, {scrollY: -window.scrollY, scale: 2});
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
        orientation: "landscape"
    });
    // Fit image, A4 width
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    // Calculate scale
    let pdfWidth = pageWidth;
    let pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    if (pdfHeight > pageHeight) {
        pdfHeight = pageHeight;
        pdfWidth = (imgProps.width * pdfHeight) / imgProps.height;
    }
    pdf.addImage(imgData, 'PNG', 10, 15, pdfWidth-20, pdfHeight-20); // basic padding
    pdf.save(filename);

    document.body.removeChild(hiddenDiv);
}

// -------------------------------------


function ProjectDetails_v1() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [projectData, setProjectData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterMonth, setFilterMonth] = useState('');
    const [exporting, setExporting] = useState(false);

    // For export menu
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const fetchProjectData = async () => {
        try {
            const token = localStorage.getItem("token");
            const headers = { Authorization: `Bearer ${token}` };

            const [projectRes, tasksRes] = await Promise.all([
                axios.get(`${MAIN_URL}/api/project-management/projects/${id}`, { headers }),
                axios.get(`${MAIN_URL}/api/project-management/project-tasks`, {
                    headers,
                    params: {
                        mode: 1,
                        organization_project_id: id,
                        per_page: 'all',
                        ...(filterMonth && { month: filterMonth }),
                    },
                }),
            ]);

            const projectInfo = projectRes.data?.data || {};
            const rawTasks = tasksRes.data?.data;
            let tasks = Array.isArray(rawTasks)
                ? rawTasks
                : Array.isArray(rawTasks?.data)
                ? rawTasks.data
                : [];

            if (filterMonth && tasks.length > 0) {
                const [yr, mo] = filterMonth.split('-').map(Number);
                tasks = tasks.filter(task => {
                    const dateStr = getScheduleDateForFilter(task);
                    if (!dateStr) return true;
                    const d = new Date(dateStr);
                    return d.getFullYear() === yr && d.getMonth() + 1 === mo;
                });
            }

            let totalEst = 0;
            let totalLogged = 0;
            tasks.forEach(task => {
                const qty = typeof task.quantity === 'number' && task.quantity > 0 ? task.quantity : 1;
                const estMins = getEstimatedMinutesRaw(task);
                totalEst += estMins !== null ? estMins * qty : 0;
                totalLogged += getLoggedMinutesRaw(task);
            });

            const fmtMins = (mins) => {
                const hr = Math.floor(mins / 60);
                const min = mins % 60;
                const parts = [];
                if (hr > 0) parts.push(`${hr} Hr`);
                if (min > 0) parts.push(`${min} min`);
                return parts.length ? parts.join(' ') : '0 min';
            };

            setProjectData({
                data: { ...projectInfo, tasks },
                total_est_time_hr_min: fmtMins(totalEst),
                total_logged_time_hr_min: fmtMins(totalLogged),
            });
        } catch (error) {
            toast.error("Failed to fetch project data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchProjectData();
    }, [id, filterMonth]);

    const renderField = (label, value, isChip = false, chipColor = 'default') => (
        <Grid item xs={12} sm={6} md={4}>
            <Box mb={1}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: '0.85rem', mb: 0.5 }}
                >
                    {label}
                </Typography>
                {isChip ? (
                    <Chip label={value || '--'} color={chipColor} size="small" />
                ) : (
                    <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ fontSize: '0.95rem' }}
                    >
                        {value ?? '--'}
                    </Typography>
                )}
            </Box>
        </Grid>
    );

    // Export handlers

    const handleExport = async (type) => {
        if (
            !projectData ||
            !projectData.data ||
            !Array.isArray(projectData.data.tasks) ||
            projectData.data.tasks.length === 0
        ) {
            toast.error("No tasks to export.");
            setAnchorEl(null);
            return;
        }
        setExporting(true);
        try {
            const filenameBase = `Project_${projectData.data.project_name || id}_Tasks`;

            if (type === 'csv') {
                const csvContent = convertTasksToCSV(projectData.data.tasks);
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                saveFile(blob, `${filenameBase}.csv`);
                toast.success("Tasks exported as CSV!");
            } else if (type === 'html') {
                const htmlContent = "<h3>Project Tasks</h3>" + convertTasksToHTML(projectData.data.tasks);
                const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8;" });
                saveFile(blob, `${filenameBase}.html`);
                toast.success("Tasks exported as HTML!");
            } else if (type === 'xls') {
                const xlsContent = convertTasksToXLS(projectData.data.tasks);
                const blob = new Blob([xlsContent], { type: "application/vnd.ms-excel" });
                saveFile(blob, `${filenameBase}.xls`);
                toast.success("Tasks exported as Excel!");
            } else if (type === 'pdf') {
                await exportTasksToPDF(projectData.data.tasks, `${filenameBase}.pdf`);
                toast.success("Tasks exported as PDF!");
            }
        } catch (e) {
            toast.error("Export failed.");
        }
        setExporting(false);
        setAnchorEl(null);
    };

    // Helper: trigger file download
    const saveFile = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={10}>
                <CircularProgress />
            </Box>
        );
    }

    if (!projectData) {
        return (
            <Typography variant="h6" align="center" mt={5}>
                No Project Data Found.
            </Typography>
        );
    }

    const {
        tasks
    } = projectData;

    return (
        <Box p={{ xs: 2, sm: 3, md: 4 }}>

            <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                    Project Details
                </Typography>
                <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
                    <MuiLink
                        color="inherit"
                        onClick={() => navigate("/project")}
                        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                        <Business fontSize="small" sx={{ mr: 0.5 }} />
                        Projects
                    </MuiLink>
                    <Typography
                        color="text.primary"
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        Project Details
                    </Typography>
                </Breadcrumbs>
                <Divider sx={{ mb: 3 }} />
            </Box>

            {/* Year and Month Filter and Export Option */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Typography variant="subtitle1" fontWeight={500}>
                    Filter by Month:
                </Typography>
                <TextField
                    type="month"
                    size="small"
                    value={filterMonth || ''}
                    onChange={e => {
                        setFilterMonth(e.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                />
                <Box ml="auto">
                    <Button
                        variant="outlined"
                        onClick={e => setAnchorEl(e.currentTarget)}
                        disabled={
                            !(
                                projectData &&
                                projectData.data &&
                                Array.isArray(projectData.data.tasks) &&
                                projectData.data.tasks.length > 0 &&
                                !exporting
                            )
                        }
                        size="small"
                    >
                        Export Tasks
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={() => setAnchorEl(null)}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('xls')}>Export as Excel (XLS)</MenuItem>
                        <MenuItem onClick={() => handleExport('html')}>Export as HTML</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Box>
            {projectData && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" gutterBottom>
                            Project: {projectData.data.project_name}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <b>Total Estimated Time:</b> {typeof projectData.total_est_time_hr_min !== 'undefined' ? projectData.total_est_time_hr_min : '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <b>Total Logged Time:</b> {typeof projectData.total_logged_time_hr_min !== 'undefined' ? projectData.total_logged_time_hr_min : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Table of Tasks */}
            {projectData && projectData.data && projectData.data.tasks && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" gutterBottom>
                            Project Tasks
                        </Typography>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 650 }} size="small">
                                <TableHead>
                                    <TableRow sx={{ background: "#f5f5f5" }}>
                                        <TableCell>Task Title</TableCell>
                                        <TableCell>Assigned Person</TableCell>
                                        <TableCell>Estimated Minutes</TableCell>
                                        <TableCell>Quantity</TableCell>
                                        <TableCell>Logged Minutes</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Schedules</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {projectData.data.tasks.length > 0 ? (
                                        projectData.data.tasks.map((task, idx) => {
                                            const assignedPerson = getAssignedPersonName(task);
                                            const estBase = getEstimatedMinutesRaw(task);
                                            const qty = typeof task.quantity === "number" && task.quantity > 0 ? task.quantity : 1;
                                            const estimatedMinutes = estBase !== null
                                                ? minsToHrMin(estBase * qty)
                                                : "-";
                                            const quantity = typeof task.quantity !== "undefined" && task.quantity !== null && task.quantity !== "-"
                                                ? task.quantity
                                                : "-";
                                            const loggedMinutes = minsToHrMin(getLoggedMinutesRaw(task));
                                            const category = getCategoryName(task);
                                            const schedules = getSchedulesDisplay(task);
                                            return (
                                                <TableRow key={task.organization_project_task_id ?? idx}>
                                                    <TableCell>
                                                        {getTaskTitle(task)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {assignedPerson}
                                                    </TableCell>
                                                    <TableCell>
                                                        {estimatedMinutes}
                                                    </TableCell>
                                                    <TableCell>
                                                        {quantity}
                                                    </TableCell>
                                                    <TableCell>
                                                        {loggedMinutes}
                                                    </TableCell>
                                                    <TableCell>
                                                        {category}
                                                    </TableCell>
                                                    <TableCell>
                                                        {schedules}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No tasks found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Project Info */}
            {/* <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

                    <Typography variant="h6" gutterBottom>Project Info</Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {renderField('Project Name', project_name)}
                        {renderField('Short Name', project_short_name)}
                        {renderField('Code', project_code)}
                        {renderField('Status', project_status, true, 'warning')}
                        {renderField('Is Billable', is_billable ? 'Yes' : 'No', true, is_billable ? 'success' : 'default')}
                        {renderField('Billing Model', billing_model)}
                        {renderField('Est. Hours Period', estimated_hours_period)}
                    </Grid>
                </CardContent>
            </Card> */}

            {/* Client Info */}
            {/* {client && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

                        <Typography variant="h6" gutterBottom>Client Info</Typography>
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            {renderField('Client Name', client.client_name)}
                            {renderField('Short Name', client.client_short_name)}
                            {renderField('Client Code', client.client_code)}
                            {renderField('Status', client.status, true, client.status === 'Active' ? 'success' : 'default')}
                            {renderField('Client Since', client.client_since?.split('T')[0])}
                            {renderField('Country ID', client.country_id)}
                            {renderField('City ID', client.city_id)}
                            {renderField('Address', client.address)}
                        </Grid>
                    </CardContent>
                </Card>
            )} */}

            {/* Category & Type */}
            {/* <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

                    <Typography variant="h6" gutterBottom>Category & Type</Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {renderField('Category', category?.project_category_name)}
                        {renderField('Type', type?.project_type_name)}
                    </Grid>
                </CardContent>
            </Card> */}

            {/* Latest Task */}
            {/* {latest_task && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

                        <Typography variant="h6" gutterBottom>Latest Task</Typography>
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            {renderField('Title', latest_task.task_title)}
                            {renderField('Assigned To', latest_task.assigned_organization_person?.person ? `${latest_task.assigned_organization_person.person.first_name || ""} ${latest_task.assigned_organization_person.person.last_name || ""}`.trim() : null)}
                            {renderField('Priority', latest_task.priority, true, 'primary')}
                            {renderField('Status', latest_task.status, true, latest_task.status === 'Blocked' ? 'error' : 'success')}
                            {renderField('Assigned Date', latest_task.assigned_date)}
                            {renderField('Estimated Mins', latest_task.estimated_minutes)}
                            {renderField('Total Est. Mins', latest_task.total_estimated_minutes)}
                        </Grid>
                    </CardContent>
                </Card>
            )} */}

            {/* All Tasks */}
            {/* <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

                    <Typography variant="h6" gutterBottom>All Tasks</Typography>
                    {tasks?.length > 0 ? (
                        tasks.map((task, idx) => (
                            <Box key={task.organization_project_task_id} mb={2}>
                                <Typography variant="subtitle1" fontWeight="bold">Task #{idx + 1}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Grid container spacing={{ xs: 2, sm: 3 }}>
                                    {renderField('Title', task.task_title)}
                                    {renderField('Priority', task.priority, true, 'info')}
                                    {renderField('Status', task.status, true, task.status === 'In Progress' ? 'warning' : 'default')}
                                    {renderField('Assigned Date', task.assigned_date)}
                                    {renderField('Quantity', task.quantity)}
                                    {renderField('Estimated Mins', task.estimated_minutes)}
                                    {renderField('Total Est. Mins', task.total_estimated_minutes)}
                                </Grid>
                            </Box>
                        ))
                    ) : (
                        <Typography>No tasks found.</Typography>
                    )}
                </CardContent>
            </Card> */}
        </Box>
    );
}

export default ProjectDetails_v1;
